import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DialogButton } from '@beeinventor/dasiot-react-component-lib';
import {
  CircularProgress,
  Dialog as MuiDialog,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  FormControl,
  styled,
  // TextareaAutosize,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { PlantType } from '../../../../types/Resource';

import OrgAutocompleteBasic from '../../../../components/OrgAutocompleteBasic';
import Input from '../../../../components/TextField/Input';

import CleanDateSvgIcon from '../../../../assets/SvgIcon/CleanDateSvgIcon';
import mainTheme from '../../../../theme';

const Dialog = styled(MuiDialog)`
  & .MuiPaper-root {
    width: 480px;
    background: ${({ theme }) => theme.color.secondary.$80};
    border-radius: 10px;
  }
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  color: white;
  font-family: 'Montserrat';
  font-weight: 700;
  padding: 8px 8px 8px 16px;

  & .MuiDialogTitle-root {
    flex: 1;
    padding: 0;
  }

  & .MuiSvgIcon-root {
    cursor: pointer;
    width: 32px;
    height: 32px;
  }
`;

const DialogContent = styled(MuiDialogContent)`
  ${({ theme }) => ({ ...theme.externalTypography.body3 })}
  padding: 20px 80px;
  color: white;

  & .MuiFormControl-root {
    width: 100%;
    margin-bottom: 10px;

    &:last-of-type {
      margin-bottom: 0;
    }

    & .MuiInputBase-root {
      width: 100%;
    }

    & textarea {
      ${({ theme }) => ({ ...theme.typography.body2 })}
      width: 100%;
      font-family: 'Noto Sans TC', 'Noto Sans';
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
      outline: none;
      color: white;
      padding: 10px;
      border-color: transparent;
      resize: none;

      &:hover {
        background: rgba(0, 0, 0, 0.3);
      }

      &:focus {
        border: 1px solid rgba(0, 0, 0, 0.5);
      }
    }
  }
`;

const DialogActions = styled(MuiDialogActions)`
  justify-content: space-between;
  padding: 24px;
`;

interface GroupDialogProps extends Omit<DialogProps, 'onClose'> {
  name?: string;
  remark?: string;
  groupId?: string;
  title: string;
  placeholder?: string;
  submitText: string;
  showDeviceTypeField?: boolean;
  plantType?: PlantType;
  operationApi: (params: {
    projectId: string;
    name: string;
    remark?: string;
    groupId: string;
    plantType?: PlantType;
  }) => Promise<any>;
  onClose: () => void;
  onSuccess: () => void;
}

const RenameDialog: React.FC<GroupDialogProps> = ({
  name,
  remark,
  groupId,
  open,
  title,
  showDeviceTypeField,
  plantType,
  onClose,
  operationApi,
  submitText,
  onSuccess: onSuccessOperation,
}) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [localName, setLocalName] = useState<string | undefined>();
  const [localRemark, setLocalRemark] = useState<string | undefined>();
  const [localPlantType, setLocalPlantType] = useState<PlantType>(
    plantType ?? 'plant',
  );
  const operationRequest = useMutation<
    any,
    unknown,
    {
      projectId: string;
      groupId: string;
      name: string;
      remark?: string;
      plantType?: PlantType;
    }
  >({
    mutationFn: async (params) => operationApi(params),
  });

  const LIST_DEVICE_TYPE = useMemo(
    () => [
      {
        id: 'plant',
        name: t(
          'project-settingJson.page.plant-management.dialog.create-plant.basic-information.device-type-plant',
        ),
      },
      {
        id: 'roofProtection',
        name: t(
          'project-settingJson.page.plant-management.dialog.create-plant.basic-information.device-type-mewp',
        ),
      },
    ],
    [t],
  );

  useEffect(() => {
    if (name !== localName && name) {
      setLocalName(name);
    }
  }, [name]);

  useEffect(() => {
    if (remark !== localRemark) {
      setLocalRemark(remark);
    }
  }, [remark]);

  useEffect(() => {
    if (plantType && plantType !== localPlantType) {
      setLocalPlantType(plantType);
    }
  }, [plantType]);

  const saveable = () => {
    if (localName === '') return false;
    return true;
  };

  const handleOperation = () => {
    if (projectId && groupId && localName) {
      operationRequest.mutate(
        {
          projectId,
          groupId,
          name: localName,
          remark: localRemark,
          ...(showDeviceTypeField ? { plantType: localPlantType } : {}),
        },
        {
          onSuccess: onSuccessOperation,
        },
      );
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  useEffect(() => {
    if (operationRequest.isError) {
      if (operationRequest.error instanceof Error) {
        alert(operationRequest.error.message);
      } else {
        alert(JSON.stringify(operationRequest.error));
      }
    }
  }, [operationRequest.isError]);

  return (
    <Dialog open={open}>
      <Header>
        <MuiDialogTitle>{title}</MuiDialogTitle>
        <CleanDateSvgIcon onClick={onClose} />
      </Header>
      <DialogContent>
        <FormControl>
          <label htmlFor="group-name-input">{t('name')}</label>
          <Input
            id="group-name-input"
            name="group-name"
            value={localName}
            onChange={(e) => setLocalName(e.currentTarget.value)}
            data-cy="input-rename-equipment-group-name"
          />
        </FormControl>
        {showDeviceTypeField && (
          <FormControl>
            <label htmlFor="group-device-type-input">
              {t(
                'project-settingJson.page.plant-management.dialog.create-plant.basic-information.device-type',
              )}
            </label>
            <OrgAutocompleteBasic
              data={LIST_DEVICE_TYPE}
              onSelect={(d) => setLocalPlantType(d.id as PlantType)}
              onBlur={() => {}}
              selectedId={localPlantType}
              data-cy="dropdown-device-type-plant-group"
            />
          </FormControl>
        )}
        {/* <FormControl>
          <label htmlFor="group-remark-input">{t('remark')}</label>
          <TextareaAutosize
            id="group-remark-input"
            name="group-remark"
            maxRows={4}
            minRows={4}
            value={localRemark}
            onChange={(e) => setLocalRemark(e.currentTarget.value)}
            data-cy="input-rename-equipment-group-remark"
          />
        </FormControl> */}
      </DialogContent>
      <DialogActions>
        <DialogButton
          sx={{
            '&.Mui-disabled': {
              color: 'white',
            },
            '&:hover, &:active': { background: mainTheme.color.secondary.$60 },
          }}
          color="secondary"
          onClick={handleOnClose}
        >
          {t('cancel')}
        </DialogButton>
        <DialogButton
          variant="contained"
          color="primary"
          disabled={operationRequest.isPending || !saveable()}
          onClick={handleOperation}
          data-cy="btn-submit-rename-equipment-group"
        >
          {operationRequest.isPending && (
            <CircularProgress
              sx={{ color: 'white', mr: '4px' }}
              size={'1rem'}
            />
          )}
          {submitText}
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
};

export default RenameDialog;
